/**
 * Pure utility functions for the Associate View Page (a.k.a. the "Staffing
 * board" / "Labor board").
 *
 * All functions in this module are pure: they take their inputs and return a
 * new value without mutating anything. This keeps the page and card
 * components thin renderers and lets the correctness properties target
 * well-scoped pure code.
 */

import { AssignmentAssociate, ProcessPath, StaffingMode, isEmptySlot } from '../assignments/models';
import { getAssociateDisplayLabel } from '../utils';

/**
 * The assignment of a single associate, split into the process-path name and
 * the (optional) station suffix so the staffing-board card can render them as
 * two distinct lines (path in accent color, station in muted gray), matching
 * the mock.
 *
 *  - `processPathName`: the function/path the associate is on, or
 *    `"Unassigned"` when the associate is in no workstation or department pool.
 *  - `station`: the workstation name for a PROCESS_LINES placement (e.g.
 *    `"line 1"`), or `""` for a DEPARTMENT placement / unassigned associate
 *    (department paths and unassigned associates have no distinct station).
 */
export interface AssociateAssignment {
  processPathName: string;
  station: string;
}

/** Label shown as the "Unassigned" process path when no placement is found. */
export const UNASSIGNED_LABEL = 'Unassigned';

/**
 * Derive the process path and station for a single associate.
 *
 * Walks `processPaths` in order and returns:
 *  - `{ processPathName, station: workstationName }` if the associate is
 *    assigned to a workstation on a PROCESS_LINES path;
 *  - `{ processPathName, station: '' }` if the associate is in a DEPARTMENT
 *    path's workstations' `assignedAssociates`;
 *  - `{ processPathName, station: '' }` if the associate is in any path's
 *    `nonWorkstationAssociates` (assigned to the function, not a station);
 *  - `{ processPathName: 'Unassigned', station: '' }` otherwise.
 *
 * Matching is by `associateId`. The function is pure and does not mutate its
 * inputs. It is the single source of truth for an associate's placement —
 * {@link collectRosterEntries} reuses it — so the two cannot drift.
 */
export function deriveAssignment(
  associate: AssignmentAssociate,
  processPaths: ProcessPath[]
): AssociateAssignment {
  for (const path of processPaths) {
    if (path.staffingMode === StaffingMode.PROCESS_LINES) {
      for (const workstation of path.workstations) {
        if (
          workstation.assignedAssociates.some(
            a => a.associateId === associate.associateId
          )
        ) {
          return {
            processPathName: path.processPathName,
            station: workstation.workstationName,
          };
        }
      }
    }
  }

  for (const path of processPaths) {
    if (path.staffingMode === StaffingMode.DEPARTMENT) {
      for (const workstation of path.workstations) {
        if (
          workstation.assignedAssociates.some(
            a => a.associateId === associate.associateId
          )
        ) {
          return { processPathName: path.processPathName, station: '' };
        }
      }
    }
  }

  // An associate can also be assigned to a function without a specific
  // workstation (e.g. DEPARTMENT/INDIRECT pool paths), in which case the facade
  // returns them in `nonWorkstationAssociates` rather than a workstation. Treat
  // that as assigned to the path with no station, so this function stays
  // consistent with {@link collectRosterEntries} (which also reads
  // `nonWorkstationAssociates`) and no caller can render such an associate as
  // "Unassigned".
  for (const path of processPaths) {
    if (
      path.nonWorkstationAssociates?.some(
        a => a.associateId === associate.associateId
      )
    ) {
      return { processPathName: path.processPathName, station: '' };
    }
  }

  return { processPathName: UNASSIGNED_LABEL, station: '' };
}

/**
 * Derive the flat station label shown on the labor board for a single
 * associate: `` `${processPathName} ${station}` `` for a workstation
 * placement, `processPathName` for a department placement, or `"Unassigned"`.
 *
 * Retained for backwards compatibility with existing tests and any caller
 * that wants the combined single-line label. New card rendering uses
 * {@link deriveAssignment} instead so it can style path and station
 * separately.
 */
export function deriveStationLabel(
  associate: AssignmentAssociate,
  processPaths: ProcessPath[]
): string {
  const { processPathName, station } = deriveAssignment(associate, processPaths);
  return station ? `${processPathName} ${station}` : processPathName;
}

/**
 * A single associate paired with their derived assignment — the unit the
 * staffing board renders.
 */
export interface RosterEntry {
  associate: AssignmentAssociate;
  assignment: AssociateAssignment;
}

/**
 * Build the FULL roster the staffing board should display, pairing each
 * associate with their process path + station.
 *
 * This is the critical difference from reading `halfData.associates` alone:
 * the real getStationAssignment API returns assigned associates ONLY inside
 * `processPaths[].workstations[].assignedAssociates` and
 * `processPaths[].nonWorkstationAssociates` — the top-level `associates` array
 * holds ONLY the UNASSIGNED pool (see `assignments/utils.ts` and the facade
 * contract). So walking `associates` and matching back into processPaths (the
 * old approach) surfaced only the unassigned pool and labelled everyone
 * "Unassigned".
 *
 * This function only ENUMERATES the associates to show — from every
 * workstation, every path's `nonWorkstationAssociates`, and finally the
 * top-level unassigned pool — and delegates the actual path/station LABEL to
 * {@link deriveAssignment}, the single source of truth. Keeping labeling in one
 * place is what stops the roster and `deriveAssignment` from drifting (an
 * associate can never be "assigned" here yet "Unassigned" there).
 *
 * Empty (planned-but-unfilled) placeholder slots — entries with no
 * `associateId` (see {@link isEmptySlot}) — are skipped so blank cards never
 * render. Each real associate is emitted once, de-duplicated by `associateId`
 * (via the `seen` set) in case the API lists someone in more than one location
 * — e.g. both a workstation and the pool. `deriveAssignment` independently
 * applies PROCESS_LINES-before-DEPARTMENT-before-nonWorkstation precedence, so
 * the label is correct regardless of which location is enumerated first.
 *
 * The input is not mutated. Order is not significant here — the board sorts
 * and groups downstream.
 */
export function collectRosterEntries(
  associates: AssignmentAssociate[],
  processPaths: ProcessPath[]
): RosterEntry[] {
  const entries: RosterEntry[] = [];
  const seen = new Set<string>();

  // Add a real associate once, deriving their placement from the single source
  // of truth ({@link deriveAssignment}) so the roster can never disagree with
  // it. Skips empty (planned-but-unfilled) placeholder slots.
  const add = (associate: AssignmentAssociate) => {
    if (isEmptySlot(associate)) return;
    const id = associate.associateId as string;
    if (seen.has(id)) return;
    seen.add(id);
    entries.push({ associate, assignment: deriveAssignment(associate, processPaths) });
  };

  // Enumerate every associate the board should show: those placed in
  // workstations, those in each path's non-workstation pool, and finally the
  // top-level unassigned pool. `deriveAssignment` decides each one's label; the
  // `seen` set de-duplicates anyone listed in more than one location.
  for (const path of processPaths) {
    for (const ws of path.workstations) {
      for (const a of ws.assignedAssociates) add(a);
    }
    for (const a of path.nonWorkstationAssociates ?? []) add(a);
  }
  for (const associate of associates) add(associate);

  return entries;
}

/**
 * The key an associate is sorted and grouped by on the staffing board: the
 * associate's login alias when present, otherwise their full name (see
 * {@link getAssociateDisplayLabel}). Matches the mock, which sorts and groups
 * by login.
 */
export function getSortKey(associate: AssignmentAssociate): string {
  return getAssociateDisplayLabel(associate.associateAlias, associate.associateName);
}

/**
 * Case-insensitive sort comparator that orders two associates by their sort
 * key (see {@link getSortKey}).
 *
 * Intended for use with `Array.prototype.sort`, e.g.
 *   `[...associates].sort(compareByDisplayName)`.
 *
 * Comparison is lexicographic on the lowercased key. Associates whose keys
 * compare equal are left in their relative input order by stable sort
 * implementations.
 */
export function compareByDisplayName(
  a: AssignmentAssociate,
  b: AssignmentAssociate
): number {
  const aKey = getSortKey(a).toLowerCase();
  const bKey = getSortKey(b).toLowerCase();
  if (aKey < bKey) return -1;
  if (aKey > bKey) return 1;
  return 0;
}

/**
 * The uppercase first letter used to bucket an associate into a letter group.
 * Non-alphabetic or empty keys bucket under `"#"` so they still render
 * somewhere deterministic rather than being dropped.
 */
export function getGroupLetter(associate: AssignmentAssociate): string {
  const key = getSortKey(associate).trim();
  if (key.length === 0) return '#';
  const first = key.charAt(0).toUpperCase();
  return /[A-Z]/.test(first) ? first : '#';
}

/**
 * A single letter group on the staffing board: the bucket letter plus the
 * associates whose sort key starts with that letter, already sorted.
 */
export interface LetterGroup {
  letter: string;
  associates: AssignmentAssociate[];
}

/**
 * Group `associates` by the uppercase first letter of their sort key and
 * return the groups in alphabetical letter order. Within each group the
 * associates are sorted by {@link compareByDisplayName}.
 *
 * The input array is not mutated. Empty input yields an empty array.
 */
export function groupAssociatesByLetter(
  associates: AssignmentAssociate[]
): LetterGroup[] {
  const buckets = new Map<string, AssignmentAssociate[]>();

  for (const associate of associates) {
    const letter = getGroupLetter(associate);
    const bucket = buckets.get(letter);
    if (bucket) {
      bucket.push(associate);
    } else {
      buckets.set(letter, [associate]);
    }
  }

  return Array.from(buckets.keys())
    .sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
    .map(letter => ({
      letter,
      associates: [...buckets.get(letter)!].sort(compareByDisplayName),
    }));
}

/**
 * Split an ordered list of letter groups into pages that each fit within
 * `capacity` associates.
 *
 * A group is never split across pages: groups are appended to the current
 * page until adding the next group would exceed `capacity`, at which point a
 * new page is started. A single group larger than `capacity` still occupies
 * its own page on its own (it is never dropped), so every associate is always
 * rendered.
 *
 * `capacity` is clamped to at least 1 so a degenerate/zero viewport
 * measurement can never produce an infinite/empty-page loop. The input is not
 * mutated. Empty input yields an empty array.
 *
 * Examples (group sizes shown):
 *   paginateGroups([A(3), B(4), C(2)], 6) => [[A, B?]...]  // A(3)+B(4)>6 → [[A],[B,C]]
 *   paginateGroups([A(10)], 6)           => [[A]]          // oversized group stands alone
 *   paginateGroups([], 6)                => []
 */
export function paginateGroups(
  groups: LetterGroup[],
  capacity: number
): LetterGroup[][] {
  const cap = Math.max(1, Math.floor(capacity));
  const pages: LetterGroup[][] = [];

  let current: LetterGroup[] = [];
  let currentCount = 0;

  for (const group of groups) {
    const size = group.associates.length;
    if (currentCount + size > cap && current.length > 0) {
      pages.push(current);
      current = [];
      currentCount = 0;
    }
    current.push(group);
    currentCount += size;
  }

  if (current.length > 0) {
    pages.push(current);
  }

  return pages;
}

/**
 * Pack an ordered list of letter groups into pages of fixed-width columns,
 * where each column must fit within `columnHeightPx`.
 *
 * This is the layout primitive the Staffing board renders from. Unlike the
 * older "cap the page total, then rebalance into columns" approach, packing
 * per column guarantees that **no column ever exceeds its height budget**, so
 * a column with `overflow: hidden` can never clip a card that was counted as
 * "on this page" — every associate is rendered on some page.
 *
 * Height accounting is per group, not per associate: a group costs
 * `groupHeaderPx` (its letter-cell header + separators/borders) plus
 * `cardHeightPx` for each associate. This charges the header overhead once per
 * group in a column, which is more accurate than subtracting a single fixed
 * allowance for the whole column.
 *
 * Packing is column-major within a page: fill column 0 until the next group
 * would overflow, move to column 1, and when all `columnsPerPage` columns are
 * full start a new page. Group order is preserved left-to-right, top-to-bottom.
 * A single group taller than one column still occupies a column on its own (it
 * is never dropped); such a group may visually clip, but this is unavoidable
 * without splitting a letter across columns, and it never hides a *different*
 * associate on another page.
 *
 * All size inputs are clamped to sane minimums so a zero/degenerate viewport
 * measurement cannot produce an empty- or infinite-page loop. The input is not
 * mutated. Empty input yields an empty array.
 *
 * @param groups          ordered letter groups
 * @param columnHeightPx  usable pixel height of one column
 * @param cardHeightPx    rendered height of one associate card
 * @param groupHeaderPx   fixed per-group overhead (letter header + borders)
 * @param columnsPerPage  columns rendered side by side (the board uses 2)
 * @returns pages, each an array of exactly `columnsPerPage` column arrays
 */
export function paginateGroupsIntoColumns(
  groups: LetterGroup[],
  columnHeightPx: number,
  cardHeightPx: number,
  groupHeaderPx: number,
  columnsPerPage = 2
): LetterGroup[][][] {
  const colHeight = Math.max(1, columnHeightPx);
  const cardH = Math.max(1, cardHeightPx);
  const headerH = Math.max(0, groupHeaderPx);
  const cols = Math.max(1, Math.floor(columnsPerPage));

  const groupCost = (group: LetterGroup): number =>
    headerH + group.associates.length * cardH;

  const pages: LetterGroup[][][] = [];
  let page: LetterGroup[][] = [];
  let column: LetterGroup[] = [];
  let columnUsed = 0;

  const startNewColumn = () => {
    page.push(column);
    column = [];
    columnUsed = 0;
  };
  const startNewPage = () => {
    // Pad the current page out to `cols` columns before pushing it.
    while (page.length < cols) page.push([]);
    pages.push(page);
    page = [];
  };

  for (const group of groups) {
    const cost = groupCost(group);
    // If this group doesn't fit in the current column and the column already
    // has something, advance to the next column (or next page).
    if (columnUsed + cost > colHeight && column.length > 0) {
      startNewColumn();
      if (page.length >= cols) {
        startNewPage();
      }
    }
    column.push(group);
    columnUsed += cost;
  }

  // Flush the trailing column and page.
  if (column.length > 0) {
    page.push(column);
  }
  if (page.length > 0) {
    startNewPage();
  }

  return pages;
}

/**
 * Split one page's letter groups into two columns, balancing by total
 * associate count so both columns are roughly the same height (matching the
 * mock's balanced two-column layout).
 *
 * NOTE: This balances a page that was sized as a single total. It can leave one
 * column taller than the other when group sizes are uneven, so it must only be
 * used where columns are allowed to grow (no clipping). The board itself packs
 * per column via {@link paginateGroupsIntoColumns} instead, which bounds each
 * column's height. Retained for callers/tests that want a simple two-column
 * balance of an already-bounded set of groups.
 *
 * Groups are assigned greedily in order to whichever column currently holds
 * fewer associates (left column wins ties, so the left column is never
 * shorter than the right for equal running totals). Group order within each
 * column is preserved. The input is not mutated.
 *
 * Returns a fixed-length tuple `[left, right]`; either column may be empty.
 */
export function splitGroupsIntoColumns(
  groups: LetterGroup[]
): [LetterGroup[], LetterGroup[]] {
  const left: LetterGroup[] = [];
  const right: LetterGroup[] = [];
  let leftCount = 0;
  let rightCount = 0;

  for (const group of groups) {
    const size = group.associates.length;
    if (leftCount <= rightCount) {
      left.push(group);
      leftCount += size;
    } else {
      right.push(group);
      rightCount += size;
    }
  }

  return [left, right];
}

/**
 * Distribute `items` into exactly `columnCount` subarrays in row-major
 * (CSS Grid `grid-auto-flow: row`) order.
 *
 * Retained for any caller/test that still depends on the row-major
 * distribution helper. For `columnCount <= 0` this throws a `RangeError`.
 *
 * Examples:
 *   distributeIntoColumns([1,2,3,4,5], 2) === [[1,3,5], [2,4]]
 *   distributeIntoColumns([],          3) === [[], [], []]
 *   distributeIntoColumns([1],         4) === [[1], [], [], []]
 */
export function distributeIntoColumns<T>(items: T[], columnCount: number): T[][] {
  if (columnCount <= 0) {
    throw new RangeError(
      `distributeIntoColumns: columnCount must be >= 1 (received ${columnCount})`
    );
  }

  const columns: T[][] = Array.from({ length: columnCount }, () => []);
  for (let i = 0; i < items.length; i++) {
    columns[i % columnCount].push(items[i]);
  }
  return columns;
}
